import { isApiError } from "@/api/_client";
import {
  addContextItemToChatSession,
  addMemberToChatSession,
  ChatSession,
  newEmptyChatSession,
  removeContextItemFromChatSession,
  removeMemberFromChatSession,
} from "@/api/chat";
import {
  GlobalSearchResultItem,
  OrganizationUser,
  useGlobalSearch,
  useOrganizationUsersInfinite,
} from "@/api/organizations";
import { useUser } from "@/api/user";
import { Button, ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toTitleCase, useFetchAllPages } from "@/lib/utils";
import { useAppContext } from "@/providers/app-context";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import FormTemplate, { FormSectionTemplate } from "../form-template";
import LoadingSpinner from "../loading-spinner";
import { ButtonLoading } from "../ui/button-loading";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import {
  SelectSearchAsync,
  SelectSearchAsyncOption,
} from "../ui/select-search-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useNavigate } from "react-router-dom";
import { useChatMessagesContext } from "@/providers/chat-message-provider";

const NewChatForm = ({
  chatSession,
  callback,
  projectId,
  onCreated,
}: {
  chatSession?: ChatSession;
  callback?: (chatSession: ChatSession) => void;
  projectId?: string;
  onCreated?: (chatSession: ChatSession) => void;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formSchema = z.object({
    title: z.string().min(1, "Portfolio name is required"),
  });
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: chatSession?.title || "",
    },
  });
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const res = await newEmptyChatSession(data.title, projectId);
    if (isApiError(res)) {
      form.setError("title", { message: res.error });
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    } else {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
      form.clearErrors();
      callback?.(res.data);
      if (onCreated) {
        onCreated(res.data);
        return;
      }
      navigate(`/chats/${res.data.id}?autoOpenConfig`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder="Enter chat session name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">Save</Button>
      </form>
    </Form>
  );
};

export const NewChatSessionDialog = ({
  children,
  projectId,
  onCreated,
  ...rest
}: {
  children?: React.ReactNode;
  projectId?: string;
  onCreated?: (chatSession: ChatSession) => void;
} & ButtonProps) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { actingOrganization } = useAppContext();
  // When a projectId is supplied the caller has already scoped to an accessible
  // project, so fine-grained folder users should still be able to create chats;
  // the backend enforces per-project access.
  const canCreate = projectId
    ? actingOrganization?.user_meta.is_admin ||
      actingOrganization?.user_meta.questions_access === "read_write"
    : actingOrganization?.user_meta.is_admin ||
      (actingOrganization?.user_meta.projects &&
        !actingOrganization?.user_meta
          .enable_projects_fine_grained_selection);
  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {canCreate ? (
        <DialogTrigger asChild>
          <Button {...rest}>
            {children || (
              <>
                <PlusIcon className="mr-2 h-4 w-4" />
                New chat
              </>
            )}
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create new chat</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <NewChatForm
            callback={() => setDialogOpen(false)}
            projectId={projectId}
            onCreated={onCreated}
          />
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
};

const ChatConfigDialog = ({
  chatSession,
  children,
  ...rest
}: {
  chatSession?: ChatSession;
  children?: React.ReactNode;
} & ButtonProps) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = window.location;
  const params = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const autoOpenConfig = params.has("autoOpenConfig");

  React.useEffect(() => {
    if (autoOpenConfig) {
      params.delete("autoOpenConfig");
      navigate(`${location.pathname}${params.toString() ? `?${params}` : ""}`, {
        replace: true,
      });
      setDialogOpen(true);
    }
  }, [autoOpenConfig, navigate, params, location.pathname]);

  const { actingOrganization } = useAppContext();
  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {actingOrganization?.user_meta.is_admin ||
      (actingOrganization?.user_meta.projects &&
        !actingOrganization?.user_meta
          .enable_projects_fine_grained_selection) ? (
        <DialogTrigger asChild>
          <Button {...rest}>
            {children || (
              <>
                <PlusIcon className="mr-2 h-4 w-4" />
                New chat
              </>
            )}
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle>Chat session configuration</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <React.Suspense fallback={<LoadingSpinner fullHeight={false} />}>
            <Tabs defaultValue="context" className="">
              <TabsList>
                <TabsTrigger value="context">Chat context</TabsTrigger>
                <TabsTrigger value="members">Members</TabsTrigger>
              </TabsList>
              <TabsContent value="context">
                <ContextSelector chatSession={chatSession} />
              </TabsContent>
              <TabsContent value="members">
                <ChatMembersConfig chatSession={chatSession} />
              </TabsContent>
            </Tabs>
          </React.Suspense>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
};

const ChatMembersConfig = ({ chatSession }: { chatSession?: ChatSession }) => {
  const { submitEvent } = useChatMessagesContext();
  const queryClient = useQueryClient();
  const currentUser = useUser();

  const usersQuery = useOrganizationUsersInfinite({});
  const users = useFetchAllPages<OrganizationUser>(usersQuery);

  const form = useForm({
    resolver: zodResolver(
      z.object({
        user_id: z.string({
          required_error: "Please select a user",
        }),
      }),
    ),
    defaultValues: {
      user_id: "",
    },
    mode: "onSubmit",
  });

  const formSections: FormSectionTemplate[] = [
    {
      fields: [
        {
          name: "user_id",
          label: "User",
          type: "select-search",
          placeholder: "Select a user",
          options:
            users.results
              .filter(
                (user: OrganizationUser) =>
                  user.email !== currentUser.data?.email &&
                  !chatSession?.users.some(
                    (chatUser) => chatUser.email === user.email,
                  ),
              )
              .map((user: OrganizationUser) => ({
                label: user.email,
                value: user.id,
              })) || [],
          extraProps: () => ({
            emptyMessage: "No users found",
            className: "min-w-[400px] md:min-w-[497px]",
            searchPlaceholder: "Search for a user to add...",
          }),
        },
      ],
    },
  ];

  const addUserMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!chatSession) return;

      await addMemberToChatSession({
        sessionId: chatSession.id,
        userId: data.user_id,
      });
      const user = users.results.find(
        (u: OrganizationUser) => u.id === data.user_id,
      );
      submitEvent({
        user: {
          first_name: currentUser.data?.first_name || "",
          last_name: currentUser.data?.last_name || "",
        },
        events: {
          user_added: [
            {
              type: "user",
              item: {
                id: data.user_id,
                name: `${user?.first_name} ${user?.last_name}`,
              },
            },
          ],
          user_removed: [],
          context_added: [],
          context_removed: [],
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
      form.reset();
    },
  });

  const selectedUser = form.watch("user_id");

  return (
    <div className="flex flex-col gap-2 py-2">
      <p className="px-2 text-sm text-muted-foreground">
        {chatSession?.users.length} member
        {chatSession?.users.length !== 1 ? "s" : ""}
      </p>
      <div className="max-h-80 space-y-2 overflow-y-auto rounded-md border p-2">
        {chatSession?.users.map((user) => (
          <ChatMemberRow key={user.id} user={user} chatSession={chatSession} />
        ))}
        {chatSession?.users.length === 0 && (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No members added yet
          </div>
        )}
      </div>
      <div className="flex w-full items-center gap-2">
        <div className="flex-1">
          <FormTemplate
            form={form}
            sections={formSections}
            onSubmit={async (data) => {
              await addUserMutation.mutateAsync(data);
            }}
            showSubmitButton={false}
            isLoading={addUserMutation.isPending}
          />
        </div>
        <ButtonLoading
          isLoading={addUserMutation.isPending}
          type="submit"
          variant="outline"
          disabled={!selectedUser}
          onClick={form.handleSubmit(async (data) => {
            await addUserMutation.mutateAsync(data);
          })}
        >
          Add member
        </ButtonLoading>
      </div>
    </div>
  );
};

const ChatMemberRow = ({
  user,
  chatSession,
}: {
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    invitation_email: string | null;
  };
  chatSession: ChatSession;
}) => {
  const queryClient = useQueryClient();
  const { submitEvent } = useChatMessagesContext();
  const currentUser = useUser();

  const removeMemberMutation = useMutation({
    mutationFn: async ({
      sessionId,
      userId,
    }: {
      sessionId: string;
      userId: string;
    }) => {
      return removeMemberFromChatSession({ sessionId, userId });
    },
    onSuccess: () => {
      submitEvent({
        user: {
          first_name: currentUser.data?.first_name || "",
          last_name: currentUser.data?.last_name || "",
        },
        events: {
          user_removed: [
            {
              type: "user",
              item: {
                id: user.id,
                name: `${user.first_name} ${user.last_name}`,
              },
            },
          ],
        },
      });
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });

  return (
    <div
      key={user.id}
      className="flex items-center justify-between rounded-md bg-muted/30 p-2"
    >
      <div className="flex flex-col">
        <span className="text-sm">
          {user.first_name} {user.last_name}
        </span>

        <div className="flex flex-wrap items-center space-x-2 divide-x divide-muted-foreground/30 text-xs text-muted-foreground">
          <span>{user.email}</span>
          {user.id === chatSession.created_by && (
            <span className="pl-2">Admin</span>
          )}
          {user.invitation_email && <span className="pl-2">Pending</span>}
        </div>
      </div>
      <ButtonLoading
        variant="ghost"
        size="sm"
        isLoading={removeMemberMutation.isPending}
        disabled={user.id === chatSession.created_by}
        onClick={() => {
          removeMemberMutation.mutate({
            sessionId: chatSession.id,
            userId: user.id,
          });
        }}
      >
        Remove
      </ButtonLoading>
    </div>
  );
};

export function ContextSelector({
  chatSession,
}: {
  chatSession?: ChatSession;
}) {
  const user = useUser();
  const { submitEvent } = useChatMessagesContext();
  const queryClient = useQueryClient();
  const [selectedContextItem, setSelectedContextItem] =
    React.useState<SelectSearchAsyncOption>();

  const mapContextItemToOption = (item: GlobalSearchResultItem) => ({
    label: item.title,
    value: item.id,
    ...item,
  });

  const addContextItemMutation = useMutation({
    mutationFn: async () => {
      console.log(selectedContextItem);
      if (!selectedContextItem) return;

      return addContextItemToChatSession({
        sessionId: chatSession!.id,
        itemType: selectedContextItem.type as string,
        itemId: selectedContextItem.id as string,
      });
    },
    onSuccess: () => {
      submitEvent({
        user: {
          first_name: user.data?.first_name || "",
          last_name: user.data?.last_name || "",
        },
        events: {
          context_added: [
            {
              type: (selectedContextItem?.type as string).toLowerCase(),
              item: {
                id: selectedContextItem?.id,
                name: selectedContextItem?.title,
              },
            },
          ],
        },
      });
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
      setSelectedContextItem(undefined);
    },
  });

  if (!chatSession) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
        No chat session selected
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 py-2">
      <p className="px-2 text-sm text-muted-foreground">
        {chatSession?.context_items.length} context item
        {chatSession?.context_items.length !== 1 ? "s" : ""}
      </p>
      <div className="max-h-80 space-y-2 overflow-y-auto rounded-md border p-2">
        {chatSession?.context_items.map((item) => (
          <ContextSelectorItemRow
            key={item.id}
            item={item}
            chatSession={chatSession}
          />
        ))}
        {chatSession?.context_items.length === 0 && (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No context items added yet
          </div>
        )}
      </div>
      <div className="flex w-full items-center gap-2">
        <div className="flex-1">
          <SelectSearchAsync
            queryFn={useGlobalSearch}
            queryParamsFn={(filter: string) => ({
              term: filter,
              resultTypes: [
                "Document" as const,
                "Project" as const,
                "Portfolio" as const,
                "Tag" as const,
                "Category" as const,
              ],
            })}
            filterQueryResultFn={(result: GlobalSearchResultItem) => {
              return !chatSession?.context_items.some(
                (item) => item.id === result.id && item.type === result.type,
              );
            }}
            mapResultToOption={mapContextItemToOption}
            value={selectedContextItem?.value}
            onChange={setSelectedContextItem}
            placeholder="Select a context item"
            searchPlaceholder="Search for a context to add..."
            emptyMessage="No context items found"
            className="min-w-[477px]"
            itemRenderer={(item) => (
              <div className="flex flex-col">
                <span className="text-sm">{item.title}</span>
                <div className="flex flex-wrap items-center space-x-2 divide-x divide-muted-foreground/30 text-xs text-muted-foreground">
                  <span>{typeof item.type === "string" ? toTitleCase(item.type) : item.type}</span>
                  {Array.isArray(item.description)
                    ? item.description.map(
                        (descriptionItem: string, index: number) => (
                          <span key={index} className="pl-2">
                            {descriptionItem}
                          </span>
                        ),
                      )
                    : item.description && (
                        <span className="pl-2">{item.description}</span>
                      )}
                </div>
              </div>
            )}
          />
        </div>
        <ButtonLoading
          isLoading={addContextItemMutation.isPending}
          type="submit"
          variant="outline"
          disabled={!selectedContextItem}
          onClick={() => addContextItemMutation.mutateAsync()}
        >
          Add to context
        </ButtonLoading>
      </div>
    </div>
  );
}

const ContextSelectorItemRow = ({
  item,
  chatSession,
}: {
  item: GlobalSearchResultItem;
  chatSession: ChatSession;
}) => {
  const queryClient = useQueryClient();
  const { submitEvent } = useChatMessagesContext();
  const user = useUser();

  const removeContextItemMutation = useMutation({
    mutationFn: async ({
      sessionId,
      itemId,
    }: {
      sessionId: string;
      itemId: string;
    }) => {
      return removeContextItemFromChatSession({
        sessionId,
        itemType: item.type as string,
        itemId,
      });
    },
    onSuccess: () => {
      submitEvent({
        user: {
          first_name: user.data?.first_name || "",
          last_name: user.data?.last_name || "",
        },
        events: {
          context_removed: [
            {
              type: (item?.type as string).toLowerCase(),
              item: {
                id: item?.id,
                name: item?.title,
              },
            },
          ],
        },
      });
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });

  return (
    <div
      key={item.id}
      className="flex items-center justify-between rounded-md bg-muted/30 p-2"
    >
      <div className="flex flex-col">
        <span className="text-sm">{item.title}</span>
        <div className="flex flex-wrap items-center space-x-2 divide-x divide-muted-foreground/30 text-xs text-muted-foreground">
          <span>{typeof item.type === "string" ? toTitleCase(item.type) : item.type}</span>
          {Array.isArray(item.description)
            ? item.description.map((descriptionItem: string, index: number) => (
                <span key={index} className="pl-2">
                  {descriptionItem}
                </span>
              ))
            : item.description && (
                <span className="pl-2">{item.description}</span>
              )}
        </div>
      </div>
      <ButtonLoading
        variant="ghost"
        size="sm"
        isLoading={removeContextItemMutation.isPending}
        onClick={() => {
          removeContextItemMutation.mutate({
            sessionId: chatSession.id,
            itemId: item.id,
          });
        }}
      >
        Remove
      </ButtonLoading>
    </div>
  );
};

export default ChatConfigDialog;
